import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Radar, ArrowRight, Eye, Cpu, Target, AlertTriangle, Sparkles, Users, Briefcase, Check,
  FileText, Box, GitBranch, Server, DollarSign, ShieldAlert, ShieldCheck, Cloud, Flag, Calendar, FileSignature, Heart, Globe, Smartphone, BrainCircuit, Network } from "lucide-react";
import { motion } from "framer-motion";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

function splitOn(str, token) {
  if (!str || !token) return [str, "", ""];
  const i = str.toLowerCase().indexOf(token.toLowerCase());
  if (i < 0) return [str, "", ""];
  return [str.slice(0, i), str.slice(i, i + token.length), str.slice(i + token.length)];
}

export default function LandingPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [heroA, heroB, heroC] = splitOn(t.landing.hero, t.landing.hero_bevor);

  return (
    <div data-testid="landing-page" className="min-h-screen pt-16">
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/27530031-6151-4b3c-b211-e0703965666a/images/ba67609f0ec690fb8ca6149dcfa92732446263f965c2d5007a1451767e1755fd.png"
            alt=""
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05050A]/60 via-[#05050A]/90 to-[#05050A]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
            className="max-w-5xl"
          >
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 flex items-center justify-center neon-border rounded-lg">
                <Radar className="w-6 h-6 text-[#00F0FF]" />
              </div>
              <span className="text-sm font-['JetBrains_Mono'] text-[#00F0FF] tracking-[0.2em] uppercase">
                {t.landing.badge}
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.1] mb-8"
            >
              {heroA}
              <span className="neon-text font-medium">{heroB}</span>
              {heroC}
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="text-lg sm:text-xl text-[#A0A0B0] leading-relaxed mb-5 max-w-2xl"
            >
              {t.landing.sub}
            </motion.p>

            <motion.p
              variants={fadeUp}
              className="text-base sm:text-lg text-[#A0A0B0] leading-relaxed mb-7 max-w-2xl"
            >
              {t.landing.sub2}
            </motion.p>

            {t.landing.bullets && t.landing.bullets.length > 0 && (
              <motion.div variants={fadeUp} className="mb-10 max-w-3xl" data-testid="hero-output-bullets">
                {t.landing.output_title && (
                  <div className="text-sm font-['JetBrains_Mono'] text-[#00F0FF] tracking-[0.18em] uppercase mb-4">
                    {t.landing.output_title}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {t.landing.bullets.map((b, i) => {
                    const BulletIcon = [FileText, Box, DollarSign, ShieldCheck, Cpu, GitBranch, FileSignature][i] || Check;
                    return (
                      <div
                        key={i}
                        data-testid={`hero-bullet-${i}`}
                        className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.025] border border-white/[0.06] hover:border-[#00F0FF]/35 hover:bg-[#00F0FF]/[0.04] transition-colors duration-200"
                      >
                        <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 bg-[#00F0FF]/10 border border-[#00F0FF]/30">
                          <BulletIcon className="w-4 h-4 text-[#00F0FF]" strokeWidth={1.8} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[13px] sm:text-sm font-['Outfit'] text-white font-medium leading-tight">{b.title}</div>
                          <div className="text-[12px] text-[#A0A0B0] leading-relaxed mt-1">{b.desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            <motion.div
              variants={fadeUp}
              className="flex flex-wrap gap-2 mb-10 max-w-2xl"
              data-testid="hero-output-pills"
            >
              {[
                { icon: FileText, label: t.landing.pill_exec, target: "deliv-card-0" },
                { icon: Box, label: t.landing.pill_3d, target: "deliv-card-1" },
                { icon: DollarSign, label: t.landing.pill_cost, target: "deliv-card-6" },
                { icon: ShieldCheck, label: t.landing.pill_compliance, target: "deliv-card-8" },
                { icon: FileSignature, label: t.landing.pill_contract, target: "contract-highlight" },
              ].map((p, i) => {
                const Icon = p.icon;
                const scrollToTarget = (e) => {
                  e.preventDefault();
                  const el = document.querySelector(`[data-testid="${p.target}"]`);
                  if (!el) return;
                  el.scrollIntoView({ behavior: "smooth", block: "center" });
                  // brief flash highlight
                  const prevBoxShadow = el.style.boxShadow;
                  const prevTransition = el.style.transition;
                  el.style.transition = "box-shadow 350ms ease-out";
                  el.style.boxShadow = "0 0 0 2px rgba(0,240,255,0.55), 0 0 28px rgba(0,240,255,0.4)";
                  window.setTimeout(() => {
                    el.style.boxShadow = prevBoxShadow;
                    window.setTimeout(() => {
                      el.style.transition = prevTransition;
                    }, 400);
                  }, 1400);
                };
                return (
                  <button
                    key={i}
                    type="button"
                    data-testid={`hero-pill-${i}`}
                    onClick={scrollToTarget}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00F0FF]/[0.06] border border-[#00F0FF]/25 text-[12px] sm:text-[13px] text-white/85 font-['Outfit'] backdrop-blur-sm cursor-pointer transition-all duration-200 hover:bg-[#00F0FF]/[0.12] hover:border-[#00F0FF]/55 hover:text-white hover:-translate-y-0.5"
                    aria-label={`${p.label} — zu den Details`}
                  >
                    <Icon className="w-3.5 h-3.5 text-[#00F0FF]" strokeWidth={2} />
                    {p.label}
                  </button>
                );
              })}
            </motion.div>

            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              {user ? (
                <>
                  <Link to="/new-project" data-testid="hero-cta-new-project">
                    <button className="btn-primary flex items-center gap-3">
                      {t.landing.cta_new}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>
                  <Link to="/dashboard" data-testid="hero-cta-dashboard">
                    <button className="btn-secondary flex items-center gap-3">
                      {t.landing.cta_dashboard}
                    </button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login" data-testid="hero-cta-login">
                    <button className="btn-primary flex items-center gap-3">
                      {t.landing.cta_login}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>
                  <Link to="/dashboard" data-testid="hero-cta-browse">
                    <button className="btn-secondary flex items-center gap-3">
                      {t.landing.cta_browse || "Projekte ansehen"}
                    </button>
                  </Link>
                </>
              )}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Problem & Loesung */}
      <section data-testid="section-problem-solution" className="py-24 relative border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Problem */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#FF3B30]/10 border border-[#FF3B30]/25">
                  <AlertTriangle className="w-4 h-4 text-[#FF3B30]" />
                </div>
                <span className="text-xs font-['JetBrains_Mono'] text-[#FF3B30] tracking-[0.2em] uppercase">
                  {t.landing.problem_label}
                </span>
              </div>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-white tracking-tight leading-tight mb-5">
                {t.landing.problem_title_a} <span className="text-[#FF3B30] font-medium">{t.landing.problem_title_b}</span>
              </h2>
              <p className="text-[#A0A0B0] leading-relaxed">
                {t.landing.problem_text}
              </p>
            </motion.div>

            {/* Loesung */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#00F0FF]/10 border border-[#00F0FF]/25">
                  <Sparkles className="w-4 h-4 text-[#00F0FF]" />
                </div>
                <span className="text-xs font-['JetBrains_Mono'] text-[#00F0FF] tracking-[0.2em] uppercase">
                  {t.landing.solution_label}
                </span>
              </div>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-white tracking-tight leading-tight mb-5">
                {t.landing.solution_title_a} <span className="neon-text font-medium">{t.landing.solution_title_b}</span> {t.landing.solution_title_c}
              </h2>
              <p className="text-[#A0A0B0] leading-relaxed">
                {t.landing.solution_text}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mehrwert Auftraggeber vs. Freelancer */}
      <section data-testid="section-value" className="py-24 relative border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mb-14"
          >
            <span className="text-xs font-['JetBrains_Mono'] text-[#00F0FF] tracking-[0.2em] uppercase">
              {t.landing.value_label}
            </span>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-white tracking-tight leading-tight mt-4">
              {t.landing.value_title_a} <span className="neon-text font-medium">{t.landing.value_title_b}</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Auftraggeber */}
            <motion.div
              data-testid="value-card-client"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className="glass-surface rounded-xl p-8 relative overflow-hidden group hover:border-[#00F0FF]/25 transition-all"
            >
              <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#00F0FF]/5 rounded-full blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#00F0FF]/10 border border-[#00F0FF]/25">
                    <Users className="w-5 h-5 text-[#00F0FF]" />
                  </div>
                  <div>
                    <span className="text-[10px] font-['JetBrains_Mono'] text-[#00F0FF] tracking-[0.2em] uppercase">
                      {t.landing.client_badge}
                    </span>
                    <h3 className="font-['Outfit'] text-xl font-medium text-white mt-1">
                      {t.landing.client_title}
                    </h3>
                  </div>
                </div>

                <ul className="space-y-4">
                  {[
                    { t: t.landing.client_b1_t, d: t.landing.client_b1_d },
                    { t: t.landing.client_b2_t, d: t.landing.client_b2_d },
                    { t: t.landing.client_b3_t, d: t.landing.client_b3_d },
                    { t: t.landing.client_b4_t, d: t.landing.client_b4_d },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#00F0FF]/15 border border-[#00F0FF]/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-[#00F0FF]" />
                      </div>
                      <div>
                        <span className="text-white text-sm font-medium block">{item.t}</span>
                        <span className="text-[#A0A0B0] text-sm leading-relaxed">{item.d}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Freelancer */}
            <motion.div
              data-testid="value-card-freelancer"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="glass-surface rounded-xl p-8 relative overflow-hidden group hover:border-[#FFB800]/25 transition-all"
            >
              <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#FFB800]/5 rounded-full blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#FFB800]/10 border border-[#FFB800]/25">
                    <Briefcase className="w-5 h-5 text-[#FFB800]" />
                  </div>
                  <div>
                    <span className="text-[10px] font-['JetBrains_Mono'] text-[#FFB800] tracking-[0.2em] uppercase">
                      {t.landing.free_badge}
                    </span>
                    <h3 className="font-['Outfit'] text-xl font-medium text-white mt-1">
                      {t.landing.free_title}
                    </h3>
                  </div>
                </div>

                <ul className="space-y-4">
                  {[
                    { t: t.landing.free_b1_t, d: t.landing.free_b1_d },
                    { t: t.landing.free_b2_t, d: t.landing.free_b2_d },
                    { t: t.landing.free_b3_t, d: t.landing.free_b3_d },
                    { t: t.landing.free_b4_t, d: t.landing.free_b4_d },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-[#FFB800]" />
                      </div>
                      <div>
                        <span className="text-white text-sm font-medium block">{item.t}</span>
                        <span className="text-[#A0A0B0] text-sm leading-relaxed">{item.d}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Universal Solution Architect — Categories Overview */}
      <section data-testid="section-categories" className="py-24 relative border-t border-white/5 overflow-hidden">
        <div className="absolute top-1/4 -right-40 w-[420px] h-[420px] bg-[#00F0FF]/[0.04] rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/3 -left-40 w-[420px] h-[420px] bg-[#A855F7]/[0.04] rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mb-14"
          >
            <span className="text-xs font-['JetBrains_Mono'] text-[#00F0FF] tracking-[0.2em] uppercase">
              {t.landing.cats_label}
            </span>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-white tracking-tight leading-tight mt-4">
              {t.landing.cats_title_a} <span className="neon-text font-medium">{t.landing.cats_title_b}</span>
            </h2>
            <p className="text-[#A0A0B0] leading-relaxed mt-5">
              {t.landing.cats_sub}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Globe, title: t.landing.cat_web_t, desc: t.landing.cat_web_d, color: "#00F0FF" },
              { icon: Smartphone, title: t.landing.cat_mobile_t, desc: t.landing.cat_mobile_d, color: "#00F0FF" },
              { icon: Box, title: t.landing.cat_3d_t, desc: t.landing.cat_3d_d, color: "#00F0FF" },
              { icon: Cloud, title: t.landing.cat_cloud_t, desc: t.landing.cat_cloud_d, color: "#3B82F6" },
              { icon: BrainCircuit, title: t.landing.cat_ai_t, desc: t.landing.cat_ai_d, color: "#A855F7" },
              { icon: ShieldCheck, title: t.landing.cat_sec_t, desc: t.landing.cat_sec_d, color: "#10B981" },
              { icon: Network, title: t.landing.cat_int_t, desc: t.landing.cat_int_d, color: "#F97316" },
              { icon: Sparkles, title: t.landing.cat_more_t, desc: t.landing.cat_more_d, color: "#FFB800" },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  data-testid={`category-card-${i}`}
                  className="group rounded-2xl p-5 bg-white/[0.02] border border-white/8 hover:border-white/20 transition-all"
                >
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                    style={{ background: `${c.color}15`, border: `1px solid ${c.color}40` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: c.color }} />
                  </div>
                  <div className="font-['Outfit'] text-[15px] text-white font-medium leading-tight mb-2">{c.title}</div>
                  <div className="text-[12px] text-[#909098] font-['Outfit'] leading-relaxed">{c.desc}</div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 rounded-xl border border-[#00F0FF]/25 bg-[#00F0FF]/[0.04]"
          >
            <div className="flex-1">
              <div className="text-xs font-['JetBrains_Mono'] tracking-[0.2em] uppercase text-[#00F0FF] mb-1.5">
                {t.landing.cats_cta_label}
              </div>
              <div className="font-['Outfit'] text-white text-base sm:text-lg font-light leading-snug">
                {t.landing.cats_cta_text}
              </div>
            </div>
            <Link
              to={user ? "/new-project" : "/login"}
              data-testid="categories-cta-btn"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#00F0FF] text-black font-['Outfit'] font-semibold text-sm whitespace-nowrap hover:bg-white transition-colors"
            >
              {t.landing.cats_cta_btn}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Deliverables grid — 12 USP cards */}
      <section data-testid="section-deliverables" className="py-24 relative border-t border-white/5 overflow-hidden">
        {/* Subtle radial accents */}
        <div className="absolute top-1/3 -left-40 w-[420px] h-[420px] bg-[#00F0FF]/[0.04] rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 -right-40 w-[420px] h-[420px] bg-[#FFB800]/[0.04] rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mb-16"
          >
            <span className="text-xs font-['JetBrains_Mono'] text-[#00F0FF] tracking-[0.2em] uppercase">
              {t.landing.deliv_label}
            </span>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-white tracking-tight leading-tight mt-4">
              {t.landing.deliv_title_a} <span className="neon-text font-medium">{t.landing.deliv_title_b}</span>
            </h2>
            <p className="text-[#A0A0B0] leading-relaxed mt-5">
              {t.landing.deliv_sub}
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          >
            {[
              { icon: FileText,    color: "#00F0FF" },
              { icon: Box,         color: "#00F0FF" },
              { icon: GitBranch,   color: "#00F0FF" },
              { icon: Eye,         color: "#00F0FF" },
              { icon: Server,      color: "#FFB800" },
              { icon: Cpu,         color: "#FFB800" },
              { icon: DollarSign,  color: "#00E5A0" },
              { icon: ShieldAlert, color: "#FF6B9D" },
              { icon: ShieldCheck, color: "#FBBF24" },
              { icon: Cloud,       color: "#8AB4F8" },
              { icon: Flag,        color: "#A89AFF" },
              { icon: Calendar,    color: "#00F0FF" },
            ].map((meta, i) => {
              const item = (t.landing.deliv || [])[i];
              if (!item) return null;
              const Icon = meta.icon;
              return (
                <motion.div
                  key={i}
                  data-testid={`deliv-card-${i}`}
                  variants={fadeUp}
                  className="glass-surface rounded-xl p-5 border border-white/5 hover:border-white/15 transition-all duration-300 group relative"
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-lg mb-4 transition-all duration-300"
                    style={{ background: `${meta.color}14`, border: `1px solid ${meta.color}30` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: meta.color }} />
                  </div>
                  <h3 className="font-['Outfit'] text-sm font-semibold text-white mb-2 leading-snug">
                    {item.t}
                  </h3>
                  <p className="text-[12px] text-[#909098] leading-relaxed">
                    {item.d}
                  </p>
                  {/* Hover glow */}
                  <div
                    className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{ boxShadow: `0 0 32px ${meta.color}15` }}
                  />
                </motion.div>
              );
            })}
          </motion.div>

          {/* Smart-Contract highlight strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            data-testid="contract-highlight"
            className="mt-10 glass-surface rounded-2xl p-8 sm:p-10 border border-[#00F0FF]/15 relative overflow-hidden"
          >
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#00F0FF]/[0.06] rounded-full blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#00F0FF]/10 border border-[#00F0FF]/30">
                    <FileSignature className="w-5 h-5 text-[#00F0FF]" />
                  </div>
                  <span className="text-[10px] font-['JetBrains_Mono'] text-[#00F0FF] tracking-[0.2em] uppercase">
                    {t.landing.contract_label}
                  </span>
                </div>
                <h3 className="font-['Outfit'] text-2xl sm:text-3xl font-light text-white tracking-tight leading-tight mb-4">
                  {t.landing.contract_title}
                </h3>
                <p className="text-[#A0A0B0] leading-relaxed text-[15px]">
                  {t.landing.contract_sub}
                </p>
              </div>
              <div className="lg:col-span-5">
                <ul className="space-y-3">
                  {[t.landing.contract_b1, t.landing.contract_b2, t.landing.contract_b3].map((label, i) => (
                    <li key={i} className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#060610]/60 border border-white/10">
                      <div className="w-6 h-6 rounded-full bg-[#00F0FF]/15 border border-[#00F0FF]/30 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-[#00F0FF]" />
                      </div>
                      <span className="text-sm text-white font-medium">{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* === Sponsor section === */}
      <section data-testid="sponsor-section" className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-40 bg-gradient-to-br from-[#FFB800]/10 via-transparent to-[#00F0FF]/10" />
        <div className="relative max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            variants={fadeUp}
            transition={{ duration: 0.7 }}
            className="rounded-2xl border border-[#FFB800]/30 bg-gradient-to-br from-[#FFB800]/[0.04] via-transparent to-[#00F0FF]/[0.04] p-8 sm:p-12 backdrop-blur-sm"
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40 flex items-center justify-center">
                <Heart className="w-4 h-4 text-[#FFB800]" />
              </span>
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#FFB800] font-['JetBrains_Mono']">
                {t.landing.sponsor_label}
              </span>
            </div>

            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-white mb-4 leading-tight">
              {t.landing.sponsor_title}
            </h2>

            <p className="text-[#A0A0B0] leading-relaxed text-[15px] sm:text-base mb-6 max-w-2xl">
              {t.landing.sponsor_sub}
            </p>

            <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
              {[t.landing.sponsor_b1, t.landing.sponsor_b2, t.landing.sponsor_b3].map((label, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 px-4 py-3 rounded-lg bg-[#060610]/60 border border-white/8"
                >
                  <Check className="w-4 h-4 text-[#FFB800] flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-white leading-snug">{label}</span>
                </li>
              ))}
            </ul>

            <p className="text-[#909098] leading-relaxed text-sm mb-7 max-w-2xl">
              {t.landing.sponsor_cta_text}
            </p>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <a
                data-testid="sponsor-paypal-btn"
                href="https://www.paypal.com/donate/?business=reto_ramstein@bluewin.ch&item_name=Project+Radar+AR&currency_code=EUR"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#FFB800] text-black font-['Outfit'] font-semibold text-sm hover:bg-white transition-colors group"
              >
                <Heart className="w-4 h-4 group-hover:scale-110 transition-transform" />
                {t.landing.sponsor_button}
                <ArrowRight className="w-4 h-4" />
              </a>
              <span className="text-[12px] text-[#606070] italic font-['JetBrains_Mono']">
                {t.landing.sponsor_thanks}
              </span>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
